import React from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function TermsOfServicePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="space-y-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
            <p className="text-muted-foreground">Last updated: May 2, 2026</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                1. Platform Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                FarmPack AI ("Platform", "we", "us") operates an online marketplace platform that connects sellers 
                of farming packages, equipment, and produce with potential buyers. FarmPack AI is NOT a party to any 
                transactions between users and acts solely as a platform provider.
              </p>
              <p>
                <strong className="text-foreground">IMPORTANT:</strong> FarmPack AI does NOT process payments, handle fulfillment, 
                or guarantee any transactions. All transactions are conducted OFF-PLATFORM between sellers and buyers. 
                We provide listing services only.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>2. User Responsibilities</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <div>
                <h3 className="font-bold text-foreground mb-2">Sellers:</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Provide accurate descriptions of all listings</li>
                  <li>Use real photos (disclose if stock photos are used)</li>
                  <li>Honest pricing with no bait-and-switch tactics</li>
                  <li>Maintain accurate contact information</li>
                  <li>Comply with local agricultural regulations</li>
                  <li>Handle all negotiations, payments, and delivery off-platform</li>
                </ul>
              </div>
              <div>
                <h3 className="font-bold text-foreground mb-2">Buyers:</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Verify seller credentials before transacting</li>
                  <li>Conduct due diligence on listings</li>
                  <li>Handle all payments and logistics independently</li>
                  <li>Report suspicious or fraudulent listings</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>3. Prohibited Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>The following items are strictly prohibited from listing on FarmPack AI:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Stolen goods or illegal products</li>
                <li>Endangered species or illegal livestock</li>
                <li>Misleading claims (fake organic certification, false yield claims)</li>
                <li>Spam or duplicate listings</li>
                <li>Contact information for competing platforms</li>
                <li>Counterfeit products or trademark violations</li>
                <li>Any content violating Ghana or Nigeria agricultural laws</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Transaction Disclaimer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                <strong className="text-foreground">NO LIABILITY:</strong> FarmPack AI shall NOT be liable for:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Any disputes between buyers and sellers</li>
                <li>Quality, accuracy, or legality of listed items</li>
                <li>Payment fraud, non-delivery, or incomplete transactions</li>
                <li>Any financial losses arising from off-platform transactions</li>
                <li>Damages resulting from reliance on listing information</li>
              </ul>
              <p className="pt-4 border-t">
                Users transact AT THEIR OWN RISK. We strongly recommend verifying seller identities, 
                inspecting goods before payment, and using secure payment methods.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>5. Seller Verification</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                FarmPack AI offers an optional verification system for sellers. Verification involves:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Government-issued ID verification</li>
                <li>Business registration document review</li>
                <li>Phone and email verification</li>
              </ul>
              <p>
                <strong className="text-foreground">Note:</strong> Verification does NOT constitute an endorsement 
                of the seller's products or business practices. It only confirms the identity of the account holder.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>6. Account Suspension & Termination</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>We reserve the right to suspend or terminate accounts for:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Violation of these Terms</li>
                <li>Posting prohibited content</li>
                <li>Fraudulent activity or scam reports</li>
                <li>Abuse of the platform (spam, harassment)</li>
                <li>Non-payment of subscription fees</li>
              </ul>
              <p>
                Appeals can be submitted to support@farmpack-ai.com. We will review appeals within 7 business days.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>7. Intellectual Property</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                By posting content (listings, reviews, images) on FarmPack AI, you grant us a non-exclusive, 
                royalty-free license to display, reproduce, and distribute such content on our platform.
              </p>
              <p>
                You retain ownership of your content. However, we reserve the right to remove any content 
                that violates our guidelines or these Terms.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>8. Limitation of Liability</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, FARMPACK AI SHALL NOT BE LIABLE FOR ANY 
                INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS 
                OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY.
              </p>
              <p>
                Our total liability for any claims related to the platform shall not exceed the amount 
                you paid to FarmPack AI in the 12 months preceding the claim.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>9. Governing Law</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>
                These Terms shall be governed by and construed in accordance with the laws of Ghana and Nigeria, 
                depending on the user's primary place of business. Any disputes shall be subject to the 
                exclusive jurisdiction of the courts in Accra, Ghana or Lagos, Nigeria.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>10. Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>For questions about these Terms, please contact us:</p>
              <ul className="list-none space-y-1">
                <li><strong className="text-foreground">Email:</strong> legal@farmpack-ai.com</li>
                <li><strong className="text-foreground">Address:</strong> FarmPack AI Legal Dept, Accra, Ghana</li>
                <li><strong className="text-foreground">Phone:</strong> +233 XX XXX XXXX</li>
              </ul>
            </CardContent>
          </Card>

          <div className="text-center pt-8 border-t">
            <p className="text-sm text-muted-foreground">
              By using FarmPack AI, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
